import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import {
  NextStepsSubscription,
  useArchiveNextStepMutation,
  useUpdateNextStepMutation,
} from '@gql'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArchiveIcon, RestoreIcon } from 'src/icons'
import canTickNextStep from '../canTickNextStep'
import { NextStepMeetingChip, NextStepThreadChip } from './NextStepChips'
import NextStepItem from './NextStepItem'

// The subscription's element type, not NextStepFragment: this page is the only
// one that asks for the tension and the meeting (ENR06-D9), so it is the only
// one whose steps carry them.
type NextStepListItem = NextStepsSubscription['next_step'][number]

// The three views of the page, exclusive by construction (ENR07-D3, D4). No
// "all": an archived step is out of circulation (D24), and bringing it back
// among what is still owed, in a list one ticks, would contradict the ground
// rules on screen.
export type NextStepsStatus = 'todo' | 'done' | 'archived'

interface Props {
  nextSteps: readonly NextStepListItem[]
  // Archived steps are read: they are reopened by unarchiving, not by ticking.
  status: NextStepsStatus
}

// A FLAT list — ENR06-D12 revokes G3-D1 and G3-D22, which grouped it by role.
// The grouping answered "what do I carry, and under which accountability"
// without a click, and that held as long as the row itself said nothing of the
// role. It now carries the role's chip (ENR06-D4), and a role filter is coming
// to the header: grouping would be a third way of saying the same thing, and a
// frozen sort competing with a filter. Yohan's reason is consistency — nothing
// justifies grouping next steps by role if tensions are not.
export default function NextStepsList({ nextSteps, status }: Props) {
  const { t } = useTranslation()
  const currentMember = useCurrentMember()
  const { orgData } = useOrgContext()
  const [updateNextStep] = useUpdateNextStepMutation()
  const [archiveNextStep] = useArchiveNextStepMutation()
  const archived = status === 'archived'

  const handleToggle = useCallback(
    (nextStep: NextStepListItem) =>
      updateNextStep({
        variables: { id: nextStep.id, values: { done: !nextStep.done } },
      }),
    [updateNextStep]
  )

  // Filing away a step one has finished oneself extends the gesture of ticking
  // it. Offered only on a DONE step and only to its assignee: archiving what is
  // not done is the circle's business, and it goes through the meeting board.
  const handleArchive = useCallback(
    (nextStep: NextStepListItem) =>
      archiveNextStep({
        variables: { id: nextStep.id, archivedAt: new Date().toISOString() },
      }),
    [archiveNextStep]
  )

  const handleUnarchive = useCallback(
    (nextStep: NextStepListItem) =>
      updateNextStep({
        variables: { id: nextStep.id, values: { archivedAt: null } },
      }),
    [updateNextStep]
  )

  // No splitting left to do: the status menu already decided what this list
  // holds (ENR07-D3). The folded "Réalisés" section it replaces was ENR06-D13
  // and G3-D3, both revoked the same day — the balance now costs one click,
  // and that price was paid knowingly for consistency with the other pages.
  //
  // The incoming order is kept as it comes: the subscription sorts oldest
  // first (ENR06-D14), the opposite of the Tensions page and on purpose — a
  // next step is a debt, not a piece of news.
  // What the global button would file away: MY finished steps, never what the
  // page happens to show (ENR06-D15). With a member filter set on someone else,
  // this list is empty — one person must not file away another's balance, and
  // the circle still sees those steps.
  // Offered in the "Réalisés" view only (ENR07-D7), where every listed step is
  // already done and unarchived — so the assignee is the only test left.
  const archivableDone = useMemo(
    () =>
      status === 'done'
        ? nextSteps.filter(
            (nextStep) =>
              !!currentMember && nextStep.memberId === currentMember.id
          )
        : [],
    [status, nextSteps, currentMember]
  )

  const handleArchiveDone = useCallback(() => {
    const archivedAt = new Date().toISOString()
    // The unitary mutation, applied to the list — no new GraphQL document,
    // which would have needed a codegen run this VPS cannot do.
    return Promise.all(
      archivableDone.map((nextStep) =>
        archiveNextStep({ variables: { id: nextStep.id, archivedAt } })
      )
    )
  }, [archivableDone, archiveNextStep])

  const renderStep = (nextStep: NextStepListItem) => {
    const isAssignee =
      !!currentMember && nextStep.memberId === currentMember.id
    // A disabled box says why; a missing one would just look broken.
    const canTick = canTickNextStep(nextStep, currentMember?.id, orgData)

    return (
      // Rows touch each other and light up on hover, like a tension's
      // (ENR06-D5, D8). The old mb={5} was there to separate two-line blocks;
      // with one line per step it was the main eater of height.
      // The row is NOT clickable as a whole: a next step carries two gestures,
      // ticking and correcting, where a tension carries only one.
      <Flex
        key={nextStep.id}
        align="center"
        gap={2}
        p={1}
        borderRadius="md"
        _hover={{ bg: 'bgItemHover' }}
      >
        {!archived && (
          <Checkbox
            isChecked={nextStep.done}
            isDisabled={!canTick}
            onChange={() => handleToggle(nextStep)}
          />
        )}
        {/* showRole is true again now that the list is flat: the role left the
            group header for the row itself (ENR06-D12). */}
        <NextStepItem
          nextStep={nextStep}
          flex={1}
          minW={0}
          editableTitle={isAssignee && !archived}
          chips={
            <>
              <NextStepThreadChip thread={nextStep.thread} />
              <NextStepMeetingChip meeting={nextStep.meeting} />
            </>
          }
        />
        {/* Icon only, label on hover (ENR06-D6). Who may see the button has not
            changed: archiving is still offered on a done step to its assignee
            alone. This changes the shape, never the right. */}
        {archived ? (
          <Tooltip label={t('NextStepsList.unarchive')}>
            <IconButton
              size="xs"
              variant="ghost"
              flexShrink={0}
              icon={<RestoreIcon size={16} />}
              aria-label={t('NextStepsList.unarchive')}
              onClick={() => handleUnarchive(nextStep)}
            />
          </Tooltip>
        ) : (
          nextStep.done &&
          isAssignee && (
            <Tooltip label={t('NextStepsList.archive')}>
              <IconButton
                size="xs"
                variant="ghost"
                flexShrink={0}
                icon={<ArchiveIcon size={16} />}
                aria-label={t('NextStepsList.archive')}
                onClick={() => handleArchive(nextStep)}
              />
            </Tooltip>
          )
        )}
      </Flex>
    )
  }

  if (nextSteps.length === 0) {
    return (
      <Text color="gray.500" _dark={{ color: 'gray.400' }} py={6}>
        {t(archived ? 'NextStepsList.emptyArchived' : 'NextStepsList.empty')}
      </Text>
    )
  }

  return (
    <Box>
      {/* The one action of this page, and the only thing above the list. Shown
          in the "Réalisés" view alone, so it appears exactly when it means
          something, glued to what it files away (ENR07-D7). In the bar it would
          read as a filter, which it is not. */}
      {archivableDone.length > 0 && (
        <Flex justify="flex-end" mb={2}>
          <Button
            size="sm"
            variant="ghost"
            fontWeight="normal"
            leftIcon={<ArchiveIcon size={16} />}
            onClick={handleArchiveDone}
          >
            {t('NextStepsList.archiveDone')}
          </Button>
        </Flex>
      )}

      {nextSteps.map(renderStep)}
    </Box>
  )
}
